import { useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Star } from 'lucide-react';
import { MAX_PINNED_WORKSPACES, type Workspace, type WorkspaceShelf } from '@unitly/contracts';
import { Sheet } from '../../ui/Sheet.tsx';
import { WorkspaceAvatar } from '../../ui/primitives.tsx';
import { useSetWorkspaceShelf, useWorkspaceShelf } from '../../api/queries.ts';
import { useOrgCtx } from '../../app/org-context.tsx';
import { useToast } from '../../ui/Toast.tsx';

export function WorkspaceShelfSheet({ open, onClose, workspaces }: { open: boolean; onClose: () => void; workspaces: Workspace[] }) {
  const { orgId } = useOrgCtx();
  const shelf = useWorkspaceShelf(orgId);
  const setShelf = useSetWorkspaceShelf(orgId);
  const toast = useToast();
  const [draft, setDraft] = useState<WorkspaceShelf | null>(null);

  useEffect(() => {
    if (open && shelf.data) setDraft(shelf.data);
  }, [open, shelf.data]);

  if (!draft) return null;
  const byId = new Map(workspaces.map((w) => [w.id, w]));
  const rows = draft.order.map((id) => byId.get(id)).filter((w): w is Workspace => Boolean(w));

  const togglePin = (id: string) => {
    const isPinned = draft.pinned.includes(id);
    if (!isPinned && draft.pinned.length >= MAX_PINNED_WORKSPACES) {
      toast(`Puoi appuntare al massimo ${MAX_PINNED_WORKSPACES} spazi.`);
      return;
    }
    setDraft({ ...draft, pinned: isPinned ? draft.pinned.filter((p) => p !== id) : [...draft.pinned, id] });
  };
  const move = (i: number, dir: -1 | 1) => {
    const order = [...draft.order];
    const j = i + dir;
    if (j < 0 || j >= order.length) return;
    [order[i], order[j]] = [order[j]!, order[i]!];
    setDraft({ ...draft, order });
  };
  const save = () => {
    if (!draft) return;
    setShelf.mutate(draft, { onSuccess: () => { toast('Spazi aggiornati'); onClose(); }, onError: () => toast('Non è stato possibile salvare') });
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Organizza spazi"
      variant="center"
      labelledBy="workspace-shelf-title"
      footer={
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={onClose}>Annulla</button>
          <button type="button" className="btn btn-primary" style={{ flex: 1 }} disabled={setShelf.isPending} onClick={save}>Salva</button>
        </div>
      }
    >
      <p className="hint" style={{ marginTop: 0 }}>Appunta fino a {MAX_PINNED_WORKSPACES} spazi per averli sempre in cima, e riordina il resto della lista.</p>
      <div className="card list">
        {rows.map((w, i) => {
          const pinned = draft.pinned.includes(w.id);
          return (
            <div key={w.id} className="section-row">
              <WorkspaceAvatar name={w.name} keyLabel={w.key} color={w.color} logoUrl={w.logoUrl} size={32} />
              <span className="grow">{w.name}</span>
              <span className="order-btns">
                <button type="button" className="icon-btn muted" aria-label="Sposta su" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp size={16} /></button>
                <button type="button" className="icon-btn muted" aria-label="Sposta giù" disabled={i === rows.length - 1} onClick={() => move(i, 1)}><ArrowDown size={16} /></button>
              </span>
              <button type="button" className="icon-btn" aria-pressed={pinned} aria-label={pinned ? `Rimuovi ${w.name} dai preferiti` : `Appunta ${w.name}`} onClick={() => togglePin(w.id)}>
                <Star size={18} fill={pinned ? 'var(--yellow)' : 'none'} color={pinned ? 'var(--yellow)' : 'var(--label-3)'} />
              </button>
            </div>
          );
        })}
      </div>
    </Sheet>
  );
}
